import { useCallback, useState } from "react";
import { searchMemory, type MemoryResult } from "../hooks/useAI";
import PageHeader from "../components/shared/PageHeader";

const KIND_OPTIONS = [
  { value: "", label: "All" },
  { value: "bod_minutes", label: "BOD minutes" },
  { value: "kb_doc", label: "Knowledge base" },
  { value: "research", label: "Research" },
  { value: "architecture", label: "Architecture" },
  { value: "plan", label: "Plans" },
  { value: "static_source", label: "Other static sources" },
];

export default function InstitutionalMemory() {
  const [q, setQ] = useState("");
  const [kind, setKind] = useState("");
  const [results, setResults] = useState<MemoryResult[]>([]);
  const [busy, setBusy] = useState(false);
  const [searched, setSearched] = useState(false);

  const run = useCallback(async () => {
    const term = q.trim();
    if (!term) return;
    setBusy(true);
    try {
      const hits = await searchMemory(term, kind ? [kind] : undefined);
      setResults(hits);
      setSearched(true);
    } finally {
      setBusy(false);
    }
  }, [q, kind]);

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <div className="mb-6">
        <PageHeader
          title="Institutional Memory"
          subtitle="Keyword search across the DCVFD knowledge corpus — 25 years of BOD minutes, the research library, architecture and plan documents, and static sources. Each result cites the source doc + chunk so the assistant's answers can be traced back."
        />
      </div>

      <div className="glass rounded-xl p-4 mb-6">
        <div className="flex flex-col sm:flex-row gap-3">
          <input
            type="text"
            className="flex-1 bg-theme-surface text-theme-primary rounded-lg px-3 py-2 border border-theme-subtle focus:outline-none focus:ring-2 focus:ring-brand-500"
            placeholder="Search the corpus — e.g. '56-hour work week', 'mentorship'"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && run()}
          />
          <select
            className="bg-theme-surface text-theme-primary rounded-lg px-3 py-2 border border-theme-subtle"
            value={kind}
            onChange={(e) => setKind(e.target.value)}
          >
            {KIND_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
          <button
            type="button"
            onClick={run}
            disabled={busy || !q.trim()}
            className="px-4 py-2 rounded-lg bg-brand-500 text-white font-medium disabled:opacity-50"
          >
            {busy ? "Searching…" : "Search"}
          </button>
        </div>
      </div>

      {searched && results.length === 0 && (
        <p className="text-sm text-theme-muted text-center py-8">
          No matching passages.
        </p>
      )}

      <div className="space-y-3">
        {results.map((r) => (
          <article key={r.chunk_id} className="glass rounded-xl p-4">
            <div className="flex items-center justify-between text-xs text-theme-muted mb-1">
              <div className="flex items-center gap-2">
                <span className="px-2 py-0.5 rounded-full bg-theme-overlay font-mono">
                  {r.kind}
                </span>
                {r.source_date && <span>{r.source_date}</span>}
              </div>
              <span className="font-mono">
                [{r.doc_id}#{r.chunk_index}]
              </span>
            </div>
            <h3 className="text-sm font-semibold text-theme-primary">
              {r.title}
            </h3>
            {r.section_path && (
              <p className="text-xs text-theme-muted mt-0.5">
                {r.section_path}
              </p>
            )}
            <p className="text-sm text-theme-secondary mt-2 leading-relaxed">
              {r.snippet}…
            </p>
          </article>
        ))}
      </div>
    </div>
  );
}
